import querystring from 'querystring';
import { window } from 'browser-monads';
import { navigate } from 'gatsby';

import { PAGE_LIST_ARRAY } from '../constants/pageList';

const PLATFORM_LOCALES = [
  { label: 'fr', value: 'fr' },
  { label: 'en', value: 'en-US' },
];

const DEFAULT_LOCALE = 'en-US';

/**
 * Retrieve the language from the navigator and format it.
 * Returns 'en-US' in case none of the platform languages are matched
 *
 * @returns {string} Formatted navigator default language
 */
function retrieveFromNavigator() {
  // several languages can be set in Chrome so we retrieve the array.
  // For Firefox < 32 and IE only one language will be fetchable so it's tranformed into array
  const navigatorLanguages = (window && window.navigator.languages) || [window && window.navigator.language] || [
      window && window.navigator.userLanguage,
    ];
  const locale = navigatorLanguages.map((language) => {
    if (!language) return DEFAULT_LOCALE;
    const extract = language.split('-')[0];
    const userLocale = PLATFORM_LOCALES.find(({ label }) => label === extract);

    return userLocale ? userLocale.value : DEFAULT_LOCALE;
  });

  // returns the first element of the array as it's the one set as first language by the user
  return locale[0] || DEFAULT_LOCALE;
}

/**
 * The preferred locale should represent the locale choosed by the user
 * This should be used in priority when formatting dates, number, currencies etc.
 * @returns {string} The preferred locale
 */
export function getPreferredLocale() {
  // if the language is set explicitly in the url, we use it
  const queryParams = window ? querystring.parse(window.location.search) : {};
  if (queryParams.language) {
    return enumLocaleToLocalStorage(queryParams.language.toUpperCase());
  }

  // else we use the cookie that should be set
  const userLocale = window && window.localStorage.getItem('locale');
  if (userLocale && PLATFORM_LOCALES.some(({ value }) => value === userLocale)) {
    return userLocale;
  }

  // if no cookie is set, use the language from the navigator
  const navigatorLocale = retrieveFromNavigator();
  if (window) window.localStorage.setItem('locale', navigatorLocale);

  return navigatorLocale;
}

export function setLocaleOnLocaleStorage(locale) {
  if (window) window.localStorage.setItem('locale', locale);
}

/**
 * Update locale in URL and reload page with translated content
 * @param {String} locale Locale
 * @returns {void}
 */
export function updateContentByLocale(locale) {
  const isHome = window && window.location.pathname === '/';

  if (!isHome) {
    const pathnameParts = window.location.pathname.split('/');

    const pageTranslations = PAGE_LIST_ARRAY.find((obj) => {
      return obj['en-US'] === pathnameParts[1] || obj.fr === pathnameParts[1];
    });

    // TODO: redirect with correct slug on project page.
    navigate(`/${pageTranslations[locale]}`, { locale });
  }
}

/**
 * Translates a locale enum value to a cookie enum value
 * @param {String} locale Locale
 * @returns {String} The cookie locale value
 */
export function enumLocaleToLocalStorage(locale) {
  switch (locale) {
    case 'FR':
      return 'fr-FR';
    case 'EN':
      return 'en-US';
    default:
      return DEFAULT_LOCALE;
  }
}

export const extractLocale = (locale) => locale.split('-')[0];
